<template>
    <div class="em-card">
        <div class="em-card-header">
            <div class="em-card-icon">
                <i class="fal fa-map-marker-alt"></i>
            </div>
            <div>
                <h3 class="em-card-title">{{ $t('events.map.title') }}</h3>
                <p class="em-card-sub">
                    {{ $t('events.map.count', { count: locatedEvents.length }) }}
                </p>
            </div>
        </div>

        <div class="em-card-body">
            <l-map
                ref="evMap"
                :center="mapCenter"
                :zoom="zoom"
                :bounds="mapBounds.length ? mapBounds : undefined"
                class="em-map"
                @update:zoom="zoom = $event"
            >
                <l-tile-layer :url="tileUrl" :attribution="tileAttribution" />
                <v-marker-cluster v-if="locatedEvents.length">
                    <l-marker
                        v-for="ev in locatedEvents"
                        :key="ev.id"
                        :lat-lng="[ev.location.lat, ev.location.lng]"
                        :icon="markerIcon(ev.type)"
                    >
                        <l-popup>
                            <div class="em-popup">
                                <span class="em-popup-type" :class="`em-popup-type--${ev.type}`">
                                    {{ $t(`events.type.${ev.type}`) }}
                                </span>
                                <p class="em-popup-title">{{ ev.title }}</p>
                                <p v-if="ev.start_date" class="em-popup-date">
                                    <i class="fal fa-calendar"></i>
                                    {{ formatDate(ev.start_date) }}
                                    <template v-if="ev.end_date"> → {{ formatDate(ev.end_date) }}</template>
                                </p>
                                <p v-if="ev.location.address" class="em-popup-address">
                                    <i class="fal fa-map-marker-alt"></i>
                                    {{ ev.location.address }}
                                </p>
                            </div>
                        </l-popup>
                    </l-marker>
                </v-marker-cluster>
            </l-map>

            <div v-if="!locatedEvents.length" class="em-empty">
                <i class="fal fa-map-marked-alt em-empty-icon"></i>
                <p>{{ $t('events.map.empty') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet'
import L from 'leaflet'

export default {
    name: 'EventsMap',
    components: { LMap, LTileLayer, LMarker, LPopup },
    props: {
        events: { type: Array, default: () => [] }
    },
    data() {
        return {
            zoom: 7,
            tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            tileAttribution: '&copy; OpenStreetMap contributors'
        }
    },
    computed: {
        locatedEvents() {
            return this.events.filter(
                (e) => e.location && e.location.lat && e.location.lng
            )
        },
        mapCenter() {
            if (!this.locatedEvents.length) return [46.603354, 1.888334]
            const lat =
                this.locatedEvents.reduce((s, e) => s + e.location.lat, 0) /
                this.locatedEvents.length
            const lng =
                this.locatedEvents.reduce((s, e) => s + e.location.lng, 0) /
                this.locatedEvents.length
            return [lat, lng]
        },
        mapBounds() {
            return this.locatedEvents.map((e) => [e.location.lat, e.location.lng])
        }
    },
    methods: {
        formatDate(iso) {
            if (!iso) return ''
            return this.moment(iso).format('L')
        },
        markerIcon(type) {
            const isProject = type === 'project'
            const color   = isProject ? '#166534' : '#0369a1'
            const bg      = isProject ? '#f0fdf4'  : '#e0f2fe'
            const border  = isProject ? '#86efac'  : '#7dd3fc'
            const icon    = isProject ? 'fa-project-diagram' : 'fa-calendar-alt'

            return L.divIcon({
                className: '',
                html: `
                    <div style="
                        width:34px;height:34px;
                        border-radius:50% 50% 50% 0;
                        transform:rotate(-45deg);
                        background:${bg};
                        border:2px solid ${border};
                        box-shadow:0 2px 6px rgba(0,0,0,0.2);
                        display:flex;align-items:center;justify-content:center;
                    ">
                        <i class="fal ${icon}" style="
                            transform:rotate(45deg);
                            color:${color};
                            font-size:13px;
                        "></i>
                    </div>`,
                iconSize: [34, 34],
                iconAnchor: [17, 34],
                popupAnchor: [0, -36]
            })
        }
    }
}
</script>

<style scoped>
.em-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.em-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--v2-border);
}

.em-card-icon {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #ecfeff;
    color: #0891b2;
    font-size: 1.1rem;
    flex-shrink: 0;
}

.em-card-title {
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--v2-text-1);
    margin: 0;
}

.em-card-sub {
    font-size: 0.8rem;
    color: var(--v2-text-4);
    margin: 3px 0 0;
}

.em-card-body {
    position: relative;
}

.em-map {
    height: calc(100vh - 340px);
    min-height: 480px;
    width: 100%;
    z-index: 1;
}

.em-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: var(--v2-text-4);
    gap: 12px;
}

.em-empty-icon {
    font-size: 2.5rem;
}

.em-popup {
    min-width: 180px;
    padding: 4px 0;
}

.em-popup-type {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    margin-bottom: 6px;
}

.em-popup-type--event {
    background: #e0f2fe;
    color: #0369a1;
}

.em-popup-type--project {
    background: #f0fdf4;
    color: #166534;
}

.em-popup-title {
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--v2-text-1);
    margin: 0 0 6px;
    line-height: 1.3;
}

.em-popup-date,
.em-popup-address {
    font-size: 0.78rem;
    color: var(--v2-text-3);
    margin: 4px 0 0;
    display: flex;
    align-items: flex-start;
    gap: 6px;
}

.em-popup-date i,
.em-popup-address i {
    flex-shrink: 0;
    margin-top: 2px;
}
</style>
